/*
 * ============================================================
 * MEWMORA · Choose Your Box — two-step purchase wizard (Phase 2B)
 * ------------------------------------------------------------
 * Step 1 Choose Your Plan → Step 2 Review Your Box →
 * NATIVE Add to Cart on final submit.
 *
 * Cat details are NO LONGER collected here. They are gathered
 * once by the six-step Play Profile quiz. This element:
 *   1. GATES access — a shopper with no valid saved profile is
 *      sent to the Play Profile quiz first (never allowed to pick
 *      a plan without a profile). Design mode + a missing gate URL
 *      fail open so the Theme Editor and any misconfig still work.
 *   2. AUTO-FILLS the seven hidden properties[…] inputs from the
 *      saved profile, and DISABLES any that are empty so a blank
 *      property never posts. No second questionnaire is shown.
 *   3. Drives a two-step flow: selecting a plan goes straight to
 *      Review. Review reads the filled hidden inputs.
 *
 * This element remains a THIN UI layer over ONE real Shopify
 * product form. It NEVER builds its own cart request:
 *  - Selecting a plan card checks that card's hidden
 *    <input name="selling_plan"> radio (One-Time = empty value,
 *    prepaid = the real allocation id rendered by Liquid). The
 *    native product-form.js then submits variant id + selling_plan
 *    + every properties[…] field together via /cart/add.js.
 *  - The Step 2 button is the form's real type="submit" name="add".
 *    Cart, checkout, Shop Pay, accelerated checkout and Markets
 *    stay entirely native.
 *
 * Fully defensive: every lookup is guarded. A half-configured
 * section quietly does nothing rather than throwing.
 * ============================================================
 */
if (!customElements.get('mewmora-choose-box')) {
  customElements.define(
    'mewmora-choose-box',
    class MewmoraChooseBox extends HTMLElement {
      constructor() {
        super();

        this.steps = Array.from(this.querySelectorAll('[data-step]'));
        this.indicators = Array.from(this.querySelectorAll('[data-step-indicator]'));
        this.planCards = Array.from(this.querySelectorAll('[data-plan-card]'));
        this.continueButton = this.querySelector('[data-step-continue]');
        this.disclosureEl = this.querySelector('[data-review-disclosure]');
        this.directDisclosureEl = this.querySelector('[data-direct-disclosure]');

        // Nothing to drive — bail out silently.
        if (this.steps.length === 0) return;

        this.config = this.readConfig();
        this.storageKey = this.config.storageKey || 'mewmora-choose-box';
        this.profilePrefix = this.config.profilePrefix || 'mewmora-play-profile';
        this.profileUrl = this.config.profileUrl || '';
        // Birthday-surprise plan key, derived from the configured delivery count
        // (money-derived cadence) — matches the Liquid data-plan-key "N-month".
        // 0/absent disables the birthday surprise. Never based on price text.
        var bsd = Number(this.config.birthdaySurpriseDeliveries || 0);
        this.birthdayPlanKey = bsd > 0 ? bsd + '-month' : '';
        this.catnipLabels = (this.config && this.config.catnipLabels) || {};

        // Hidden line item property inputs (filled from the saved profile).
        this.propertyInputs = Array.from(this.querySelectorAll('[data-cat-field]'));

        // GATE FIRST. If the shopper has no valid profile, redirect to the quiz
        // before any plan can be chosen. Returns true when it redirected.
        if (this.enforceProfileGate()) return;

        this.currentStep = 1;
        // Default selection = the first card (One-Time), whose radio is
        // checked in Liquid. Keeps the native form always submittable.
        this.selectedCard = this.planCards[0] || null;

        this.fillPropertiesFromProfile();
        this.renderProfileBanner();
        this.refreshBirthdaySurprise();

        this.bindPlanSelection();
        this.bindNavigation();
        this.bindEditLinks();
        this.bindPageShow();
        this.bindSubmitSync();

        this.restoreState();

        // Ensure EXACTLY ONE card shows the selected state on load (default =
        // One-Time). This also checks its selling_plan radio and clears the
        // others, so the featured "MOST POPULAR" card is never pre-selected.
        if (this.selectedCard) this.selectPlan(this.selectedCard);

        this.goToStep(this.currentStep, { focus: false, save: false });
      }

      readConfig() {
        const node = this.querySelector('[data-mewmora-config]');
        if (!node) return {};
        try {
          return JSON.parse(node.textContent) || {};
        } catch (e) {
          return {};
        }
      }

      /* -------------------- profile gate -------------------- */
      // Design mode always fails open (so the Theme Editor is usable). Otherwise
      // require a completed profile with a play-profile value. If it's missing
      // AND a gate URL is configured, redirect to the quiz (carrying country).
      // If no gate URL is set we fail open rather than trap the shopper.
      enforceProfileGate() {
        if (this.isDesignMode()) return false;
        // The quiz can be intentionally completed with every scored question
        // skipped. Allow that explicit handoff through to plan selection while
        // keeping the profile banner hidden.
        if (this.hasNoProfileHandoff()) return false;
        if (this.hasValidProfile()) return false;
        if (!this.profileUrl) return false; // gate disabled — never block

        try {
          const url = this.appendCountry(this.profileUrl);
          window.location.replace(url);
          return true;
        } catch (e) {
          return false;
        }
      }

      hasNoProfileHandoff() {
        try {
          return (new URLSearchParams(window.location.search).get('profile') || '').toLowerCase().trim() === 'none';
        } catch (e) {
          return false;
        }
      }

      isDesignMode() {
        try {
          if (window.Shopify && window.Shopify.designMode) return true;
        } catch (e) {
          /* ignore */
        }
        try {
          if (document.documentElement.classList.contains('shopify-design-mode')) return true;
        } catch (e) {
          /* ignore */
        }
        return false;
      }

      // A profile is valid when the unified store says completed with a play
      // profile value, OR the shared Play Profile sessionStorage carries a
      // result / a ?profile= param is present. Any one is enough.
      hasValidProfile() {
        try {
          if (window.MewmoraProfileStore && typeof window.MewmoraProfileStore.load === 'function') {
            const p = window.MewmoraProfileStore.load();
            if (p && p.completed === true && (p.playProfile || '') !== '') return true;
          }
        } catch (e) {
          /* ignore */
        }
        // Fallbacks: explicit ?profile= handoff or a stored quiz result.
        if (this.readProfileKey() !== '') return true;
        return false;
      }

      // Append the current page's country param (US test only) to a URL without
      // clobbering existing params. Never hardcodes a country.
      appendCountry(url) {
        try {
          const here = new URLSearchParams(window.location.search);
          const country = here.get('country');
          if (!country) return url;
          const hashIndex = url.indexOf('#');
          const hash = hashIndex === -1 ? '' : url.slice(hashIndex);
          const noHash = hashIndex === -1 ? url : url.slice(0, hashIndex);
          const joiner = noHash.indexOf('?') === -1 ? '?' : '&';
          if (/[?&]country=/.test(noHash)) return url;
          return noHash + joiner + 'country=' + encodeURIComponent(country) + hash;
        } catch (e) {
          return url;
        }
      }

      /* -------------------- fill hidden properties -------------------- */
      // Read the saved profile and write each hidden properties[…] input. An
      // input with a value is ENABLED (so it posts); an empty one stays DISABLED
      // (so no blank property is ever submitted). We never invent a value the
      // quiz did not collect.
      fillPropertiesFromProfile() {
        let profile = {};
        try {
          if (window.MewmoraProfileStore && typeof window.MewmoraProfileStore.load === 'function') {
            profile = window.MewmoraProfileStore.load() || {};
          }
        } catch (e) {
          profile = {};
        }

        // Map the play profile key to the display value used by the order.
        const profileKey = (
          this.hasNoProfileHandoff() ? '' : profile.playProfile || this.readProfileKey() || ''
        ).toString();
        const profileMap = { chase: 'CHASE', pounce: 'POUNCE', bond: 'BOND', mixed: 'MIXED' };
        const playProfileValue = profileMap[profileKey.toLowerCase()] || profileKey.toUpperCase();

        const toy =
          Array.isArray(profile.toyPreferences) && profile.toyPreferences.length
            ? profile.toyPreferences[0]
            : '';

        const values = {
          'Cat Name': profile.catName || '',
          'Cat Age': profile.catAge || '',
          'Number of Cats': profile.numberOfCats || '',
          'Play Profile': playProfileValue || '',
          // Catnip is stored as a stable key; show its display label. '' stays ''
          // (skipped/unanswered) so no blank/"No" ever posts.
          'Catnip Preference': this.catnipDisplay(profile.catnipPreference),
          'Favorite Toy Style': toy || '',
          'Special Notes': profile.notes || '',
          'Cat Birthday': this.formatBirthday(profile.birthdayMonth, profile.birthdayYear),
        };

        this.propertyInputs.forEach((input) => {
          // Birthday Surprise is plan-dependent, set by refreshBirthdaySurprise()
          // — never filled here, so it can't post on a non-12-month order.
          if (input.hasAttribute('data-birthday-surprise')) return;
          const name = input.getAttribute('data-cat-field');
          const value = (values[name] != null ? String(values[name]) : '').trim();
          input.value = value;
          // Enable only non-empty properties so blanks never post.
          input.disabled = value === '';
        });
      }

      // Map a catnip key to its display label (from config). Unknown/blank → ''.
      // Keeps "no_interest", "not_sure" and skipped ('') as distinct states.
      catnipDisplay(key) {
        const k = (key || '').toString().trim();
        if (k === '') return '';
        return (this.catnipLabels && this.catnipLabels[k]) || '';
      }

      // Read the raw saved birthday parts from the store (month/year).
      readBirthdayParts() {
        try {
          if (window.MewmoraProfileStore && typeof window.MewmoraProfileStore.load === 'function') {
            const p = window.MewmoraProfileStore.load() || {};
            return { month: p.birthdayMonth || '', year: p.birthdayYear || '' };
          }
        } catch (e) {
          /* ignore */
        }
        return { month: '', year: '' };
      }

      hasBirthday() {
        const b = this.readBirthdayParts();
        return this.formatBirthday(b.month, b.year) !== '';
      }

      isBirthdayPlanSelected() {
        if (!this.birthdayPlanKey || !this.selectedCard) return false;
        return (this.selectedCard.getAttribute('data-plan-key') || '') === this.birthdayPlanKey;
      }

      // Enable the "Birthday Surprise" line item property ONLY when the birthday
      // plan is selected AND a real birthday exists. Otherwise it stays disabled
      // and empty, so it never posts on other plans or without a birthday.
      refreshBirthdaySurprise() {
        const input = this.querySelector('[data-birthday-surprise]');
        if (!input) return;
        if (this.isBirthdayPlanSelected() && this.hasBirthday()) {
          input.value = 'Included';
          input.disabled = false;
        } else {
          input.value = '';
          input.disabled = true;
        }
      }

      getFieldValue(name) {
        const match = this.propertyInputs
          ? this.propertyInputs.find((f) => f.getAttribute('data-cat-field') === name)
          : null;
        return match ? (match.value || '').trim() : '';
      }

      /* -------------------- personalized banner (display-only) -------------------- */
      // Reveal the top-of-page recap of the cat's Play Profile, filled from the
      // SAME saved profile that fills the hidden properties. It collects nothing
      // and is not part of the form. If no play profile can be resolved we leave
      // the banner hidden (a gated direct visitor never sees an empty banner).
      // Facts with no saved value stay hidden — nothing is invented.
      renderProfileBanner() {
        const banner = this.querySelector('[data-profile-banner]');
        if (!banner) return;

        const cfg = (this.config && this.config.banner) || {};

        let profile = {};
        try {
          if (window.MewmoraProfileStore && typeof window.MewmoraProfileStore.load === 'function') {
            profile = window.MewmoraProfileStore.load() || {};
          }
        } catch (e) {
          profile = {};
        }

        // Resolve the play-style key (store first, then ?profile= / sessionStorage).
        const rawKey = (
          this.hasNoProfileHandoff() ? '' : profile.playProfile || this.readProfileKey() || ''
        ).toString().toLowerCase().trim();
        const key = ['chase', 'pounce', 'bond', 'mixed'].indexOf(rawKey) !== -1 ? rawKey : '';
        // No profile → collapse the banner. This is written idempotently (not a
        // bare return) so a re-render after the profile is cleared removes any
        // stale content instead of leaving the last shopper's result on screen.
        if (key === '') {
          banner.setAttribute('hidden', '');
          banner.setAttribute('aria-hidden', 'true');
          banner.removeAttribute('data-profile-key');
          return;
        }

        // Name: real value, else the configured fallback (never a fabricated name).
        const rawName = (profile.catName || '').toString().trim();
        const fallbackName = (cfg.fallbackName || '').toString().trim();
        const displayName = rawName || fallbackName;

        // Profile display name from settings (mirrors the property value casing).
        const profileNames = cfg.profileNames || {};
        const profileDisplay = (profileNames[key] || key.toUpperCase()).toString();

        const fill = (tmpl) =>
          (tmpl || '')
            .toString()
            .split('[name]')
            .join(displayName)
            .split('[profile]')
            .join(profileDisplay)
            .trim();

        // Eyebrow (optional element — only present when configured).
        const eyebrowEl = banner.querySelector('[data-banner-eyebrow]');
        if (eyebrowEl) {
          const text = fill(cfg.eyebrow);
          if (text) {
            eyebrowEl.textContent = text;
            eyebrowEl.removeAttribute('hidden');
          } else {
            eyebrowEl.setAttribute('hidden', '');
          }
        }

        // Title.
        const titleEl = banner.querySelector('[data-banner-title]');
        if (titleEl) {
          const text = fill(cfg.title);
          if (text) {
            titleEl.textContent = text;
            titleEl.removeAttribute('hidden');
          } else {
            titleEl.setAttribute('hidden', '');
          }
        }

        // Blurb, chosen by play style.
        const blurbEl = banner.querySelector('[data-banner-blurb]');
        if (blurbEl) {
          const blurbs = cfg.blurbs || {};
          const text = fill(blurbs[key]);
          if (text) {
            blurbEl.textContent = text;
            blurbEl.removeAttribute('hidden');
          } else {
            blurbEl.setAttribute('hidden', '');
          }
        }

        // Facts: birthday, life stage, favorite toy. Each hides when empty.
        const facts = {
          birthday: this.formatBirthday(profile.birthdayMonth, profile.birthdayYear),
          stage: (profile.catAge || '').toString().trim(),
          toy:
            Array.isArray(profile.toyPreferences) && profile.toyPreferences.length
              ? String(profile.toyPreferences[0]).trim()
              : '',
          catnip: this.catnipDisplay(profile.catnipPreference),
        };
        Object.keys(facts).forEach((name) => {
          const row = banner.querySelector('[data-banner-fact="' + name + '"]');
          if (!row) return;
          const value = facts[name];
          const valueEl = row.querySelector('[data-banner-fact-value]');
          if (value && valueEl) {
            valueEl.textContent = value;
            row.removeAttribute('hidden');
          } else {
            row.setAttribute('hidden', '');
          }
        });

        // Per-style accent, mirroring the Play Profile result cards.
        banner.setAttribute('data-profile-key', key);
        banner.removeAttribute('hidden');
        banner.removeAttribute('aria-hidden');
      }

      // Format a saved birthday (month 1-12 + year) into a friendly label. Any
      // missing/invalid part yields '' so the fact row hides. Never fabricates.
      formatBirthday(month, year) {
        const m = month ? parseInt(month, 10) : 0;
        const y = year ? parseInt(year, 10) : 0;
        const names = [
          'January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December',
        ];
        if (y && m >= 1 && m <= 12) return names[m - 1] + ' ' + y;
        if (y) return String(y);
        return '';
      }

      readProfileKey() {
        // 1) Explicit ?profile= handoff from the Play Profile CTA / redirect.
        try {
          const param = new URLSearchParams(window.location.search).get('profile');
          if (param) {
            const norm = param.toLowerCase().trim();
            // Explicit no-profile completion must also suppress fallback scans,
            // otherwise an older session result could reappear as MIXED.
            if (norm === 'none') return '';
            if (['chase', 'pounce', 'bond', 'mixed'].indexOf(norm) !== -1) return norm;
          }
        } catch (e) {
          /* ignore */
        }
        // 2) Shared Play Profile sessionStorage (read-only scan).
        try {
          for (let i = 0; i < window.sessionStorage.length; i++) {
            const k = window.sessionStorage.key(i);
            if (!k || k.indexOf(this.profilePrefix) !== 0) continue;
            const parsed = JSON.parse(window.sessionStorage.getItem(k));
            if (parsed && parsed.result) return String(parsed.result).toLowerCase().trim();
          }
        } catch (e) {
          /* ignore */
        }
        return '';
      }

      /* -------------------- Step 1: plan selection -------------------- */
      bindPlanSelection() {
        this.planCards.forEach((card) => {
          const button = card.querySelector('[data-plan-select]');
          if (button) {
            button.addEventListener('click', (event) => {
              event.stopPropagation();
              this.selectPlan(card);
            });
          }

          // Whole card acts as a button (mouse + keyboard).
          card.addEventListener('click', () => {
            this.selectPlan(card);
          });
          card.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
              event.preventDefault();
              this.selectPlan(card);
            }
          });
        });
      }

      // Selecting a card checks ITS hidden selling_plan radio, so the native
      // form submits the right plan (empty for One-Time). We never read or
      // write a plan id in JS — only toggle which real radio is checked.
      selectPlan(card) {
        if (!card) return;
        this.selectedCard = card;
        this.planCards.forEach((c) => {
          const isSel = c === card;
          c.classList.toggle('is-selected', isSel);
          c.setAttribute('aria-pressed', String(isSel));
          const radio = c.querySelector('[data-plan-radio]');
          if (radio) radio.checked = isSel;
        });
        // Recompute the birthday-surprise property whenever the plan changes.
        this.refreshBirthdaySurprise();
        if (this.directDisclosureEl) {
          const key = card.getAttribute('data-plan-key') || '';
          this.directDisclosureEl.toggleAttribute('hidden', key === '' || key === 'one-time');
        }
        this.saveState();
      }

      /* -------------------- Step navigation -------------------- */
      bindNavigation() {
        this.querySelectorAll('[data-step-back]').forEach((button) => {
          button.addEventListener('click', () => this.goToStep(this.currentStep - 1));
        });

        // Kept for compatibility; the two-step flow has no explicit continue.
        if (this.continueButton) {
          this.continueButton.addEventListener('click', () => this.goToStep(2));
        }
      }

      /* -------------------- read-back on back/forward (bfcache) -------------------- */
      // A custom element's constructor does NOT re-run when a page is restored
      // from the back/forward cache (e.g. the shopper taps the browser Back
      // button from the cart, or returns after editing the quiz). Without this,
      // the banner, hidden properties and review could show a stale snapshot.
      // On a persisted pageshow we re-read the saved profile and re-render.
      // Fully guarded: any failure leaves the page exactly as the server sent it.
      bindPageShow() {
        try {
          window.addEventListener('pageshow', (event) => {
            if (!event || !event.persisted) return; // only bfcache restores
            this.refreshFromProfile();
          });
        } catch (e) {
          /* listener unavailable — ignore */
        }
      }

      // Refresh the hidden line item properties in the capture phase immediately
      // before Shopify's native product-form submit handler builds FormData. This
      // protects against a profile being edited in another tab while this review
      // page remains open, without replacing or bypassing the native cart request.
      bindSubmitSync() {
        const form = this.querySelector('form');
        if (!form) return;
        form.addEventListener(
          'submit',
          () => {
            this.fillPropertiesFromProfile();
            this.refreshBirthdaySurprise();
          },
          true
        );
      }

      // Re-read the saved profile into the hidden properties and the banner, and
      // refresh the review if it's the visible step. Never re-gates and never
      // touches the plan selection or the native form contract.
      refreshFromProfile() {
        try {
          this.fillPropertiesFromProfile();
        } catch (e) {
          /* ignore */
        }
        try {
          this.renderProfileBanner();
        } catch (e) {
          /* ignore */
        }
        try {
          this.refreshBirthdaySurprise();
        } catch (e) {
          /* ignore */
        }
        try {
          if (this.currentStep === 2) this.renderReview();
        } catch (e) {
          /* ignore */
        }
      }

      /* -------------------- Step 2 edit links -------------------- */
      bindEditLinks() {
        this.querySelectorAll('[data-edit-step]').forEach((link) => {
          link.addEventListener('click', () => {
            const target = Number(link.getAttribute('data-edit-step'));
            // Never clears saved data — goToStep preserves all field values.
            this.goToStep(target);
          });
        });
        // "Edit Cat Details" is a real link back to the quiz; carry context.
        const editProfile = this.querySelector('[data-edit-profile]');
        if (editProfile) {
          const href = editProfile.getAttribute('href') || '';
          if (href) editProfile.setAttribute('href', this.appendContext(href));
        }
        // "Add Birthday" → the quiz's birthday step (edit=birthday), carrying
        // country + preview_theme_id so the draft-theme preview isn't lost. All
        // other answers and the selected plan are preserved (stored separately).
        const addBirthday = this.querySelector('[data-add-birthday]');
        if (addBirthday) {
          const href = addBirthday.getAttribute('href') || '';
          if (href) {
            const withEdit = this.appendParam(href, 'edit', 'birthday');
            addBirthday.setAttribute('href', this.appendContext(withEdit));
          }
        }
      }

      // Append a single key=value if not already present (keeps hash intact).
      appendParam(url, key, value) {
        try {
          if (new RegExp('[?&]' + key + '=').test(url)) return url;
          const hashIndex = url.indexOf('#');
          const hash = hashIndex === -1 ? '' : url.slice(hashIndex);
          const noHash = hashIndex === -1 ? url : url.slice(0, hashIndex);
          const joiner = noHash.indexOf('?') === -1 ? '?' : '&';
          return noHash + joiner + key + '=' + encodeURIComponent(value) + hash;
        } catch (e) {
          return url;
        }
      }

      // Carry current-page context params (country + preview_theme_id) onto a URL.
      appendContext(url) {
        let out = this.appendCountry(url);
        try {
          const here = new URLSearchParams(window.location.search);
          const preview = here.get('preview_theme_id');
          if (preview) out = this.appendParam(out, 'preview_theme_id', preview);
        } catch (e) {
          /* ignore */
        }
        return out;
      }

      goToStep(step, options) {
        const opts = options || {};
        const target = Math.min(Math.max(step, 1), this.steps.length);

        // Guard: can't reach step 2+ without a selected plan (always true here
        // since One-Time defaults selected, but kept defensive).
        if (target >= 2 && !this.selectedCard) {
          this.currentStep = 1;
        } else {
          this.currentStep = target;
        }

        this.steps.forEach((section) => {
          const isActive = Number(section.getAttribute('data-step')) === this.currentStep;
          section.toggleAttribute('hidden', !isActive);
        });

        this.indicators.forEach((item) => {
          const num = Number(item.getAttribute('data-step-indicator'));
          item.classList.toggle('is-complete', num < this.currentStep);
          item.classList.toggle('is-active', num === this.currentStep);
          if (num === this.currentStep) {
            item.setAttribute('aria-current', 'step');
          } else {
            item.removeAttribute('aria-current');
          }
        });

        if (this.currentStep === 2) this.renderReview();

        if (opts.save !== false) this.saveState();

        if (opts.focus !== false) {
          const activeSection = this.steps.find(
            (s) => Number(s.getAttribute('data-step')) === this.currentStep
          );
          const eyebrow = activeSection && activeSection.querySelector('[data-step-eyebrow]');
          if (eyebrow) eyebrow.focus({ preventScroll: false });
        }
      }

      /* -------------------- Step 2: review -------------------- */
      renderReview() {
        const planName = this.selectedCard
          ? this.selectedCard.getAttribute('data-plan-title') || ''
          : '';
        this.setReview('plan', planName);

        ['Cat Name', 'Cat Age', 'Play Profile', 'Cat Birthday', 'Catnip Preference', 'Favorite Toy Style', 'Special Notes'].forEach((name) => {
          const value = this.getFieldValue(name);
          this.setReview(name, value);
          const row = this.querySelector(`[data-review-row="${CSS.escape(name)}"]`);
          if (row) row.toggleAttribute('hidden', value === '');
        });

        this.renderBirthdaySurpriseReview();

        const billing = this.selectedCard
          ? this.selectedCard.getAttribute('data-billing') || ''
          : '';
        this.setReview('billing', billing);

        const price = this.selectedCard
          ? (this.selectedCard.getAttribute('data-price') || '').trim()
          : '';
        if (price !== '') this.setReview('total', price);

        // Prepaid disclosure shows only when a prepaid plan is selected.
        if (this.disclosureEl) {
          const key = this.selectedCard ? this.selectedCard.getAttribute('data-plan-key') || '' : '';
          const isPrepaid = key !== '' && key !== 'one-time';
          this.disclosureEl.toggleAttribute('hidden', !isPrepaid);
        }
      }

      setReview(key, value) {
        const cell = this.querySelector(`[data-review="${CSS.escape(key)}"]`);
        if (cell) cell.textContent = value && value !== '' ? value : '—';
      }

      // Birthday-surprise review block. Shown ONLY when the birthday plan is
      // selected. Then exactly one of two states:
      //   • birthday present → "Included" + the formatted date
      //   • no birthday      → nudge + "Add Birthday" (never blocks purchase)
      // For any other plan the whole block is hidden.
      renderBirthdaySurpriseReview() {
        const block = this.querySelector('[data-birthday-block]');
        if (!block) return;
        const included = block.querySelector('[data-birthday-included]');
        const prompt = block.querySelector('[data-birthday-prompt]');
        const dateEl = block.querySelector('[data-birthday-date]');

        if (!this.isBirthdayPlanSelected()) {
          block.setAttribute('hidden', '');
          if (included) included.setAttribute('hidden', '');
          if (prompt) prompt.setAttribute('hidden', '');
          return;
        }

        block.removeAttribute('hidden');
        const b = this.readBirthdayParts();
        const date = this.formatBirthday(b.month, b.year);
        if (date !== '') {
          if (dateEl) dateEl.textContent = date;
          if (included) included.removeAttribute('hidden');
          if (prompt) prompt.setAttribute('hidden', '');
        } else {
          if (included) included.setAttribute('hidden', '');
          if (prompt) prompt.removeAttribute('hidden');
        }
      }

      /* -------------------- State persistence (sessionStorage) -------------------- */
      // Persists ONLY the plan + step. Cat details live in the unified profile
      // store (written by the quiz) and are re-read on load — never duplicated
      // here, so the two data sources can't drift.
      saveState() {
        const data = {
          step: this.currentStep,
          plan: this.selectedCard ? this.selectedCard.getAttribute('data-plan-key') : '',
        };
        try {
          window.sessionStorage.setItem(this.storageKey, JSON.stringify(data));
        } catch (e) {
          /* storage unavailable — ignore */
        }
      }

      restoreState() {
        let data = null;
        try {
          const raw = window.sessionStorage.getItem(this.storageKey);
          if (raw) data = JSON.parse(raw);
        } catch (e) {
          data = null;
        }
        if (!data) return;

        // Restore selected plan (matched by plan key against a real card).
        if (data.plan) {
          const match = this.planCards.find(
            (c) => (c.getAttribute('data-plan-key') || '') === data.plan
          );
          if (match) this.selectPlan(match);
        }

        // Restore step (only if reachable).
        if (data.step) {
          const wanted = Number(data.step);
          this.currentStep = this.selectedCard ? wanted : 1;
        }
      }
    }
  );
}
